/**
 * The screen somebody sees until they confirm their address.
 *
 * This stands in front of the application rather than beside it: the server
 * refuses everything group-scoped while the address is unconfirmed, so an
 * interface that let people carry on would only be showing them screens whose
 * every request fails.
 *
 * What it deliberately keeps reachable is the way out — send the link again,
 * change the address, and sign out. The server draws the same line, refusing
 * group procedures while allowing the handful that resolve the situation,
 * because a gate that also blocked those would leave somebody holding an
 * account they can neither use nor escape.
 *
 * Changing the address matters more than it looks. Since an address can now be
 * edited, somebody can mistype one and land here with a confirmation sent
 * somewhere they cannot read. Without a route back to the account screen that
 * is a locked door with the key on the other side of it, and no amount of
 * "Send it again" opens it.
 */

import { useMutation } from "@tanstack/react-query";
import { useT } from "../../lib/i18n.js";
import { Link, useRouterState } from "@tanstack/react-router";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { useAuth } from "../../lib/auth.js";
import type { ReactNode } from "react";
import { Button } from "../../components/ui/Button.js";
import { Logo } from "../../components/ui/Logo.js";
import { useToast } from "../../components/ui/Toast.js";
import "./auth.css";

export function VerifyEmailGate({ children }: { children: ReactNode }) {
  const t = useT();
  const auth = useAuth();
  const { toast } = useToast();

  const resend = useMutation({
    mutationFn: () => trpcClient.auth.sendVerificationEmail.mutate(),
    /*
     * One message whatever happened. The procedure deliberately reports nothing
     * about whether a message was accepted, so claiming "sent" here would be
     * inventing a fact the client was never given.
     */
    onSuccess: () =>
      toast("If that address can receive mail, a link is on its way."),
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  // The one screen that has to stay open: it is where a wrong address is put
  // right. Nothing on it is group-scoped, so the server allows it too.
  const onAccountScreen = useRouterState({
    select: (state) => state.location.pathname.startsWith("/account"),
  });

  const user = auth.user;
  // Accounts created before an address was asked for have nothing to confirm,
  // and the server does not gate them either.
  if (!user || user.emailVerified || !user.email) return <>{children}</>;
  if (onAccountScreen) return <>{children}</>;

  return (
    <div className="fc-verify-gate">
      <div className="fc-verify-gate__card">
        <Logo size={28} withWordmark />
        <h1 className="fc-verify-gate__title">{t("Confirm your email")}</h1>
        <p className="fc-verify-gate__text">
          We sent a link to <strong>{user.email}</strong>. Follow it and you are
          in.
        </p>
        <p className="fc-verify-gate__text">
          Nothing works until then — the address is how you get back in if you
          forget your password, so it has to be one you can actually reach.
        </p>

        <div className="fc-verify-gate__actions">
          <Button onClick={() => resend.mutate()} loading={resend.isPending}>
            {t("Send it again")}
          </Button>
          <Link className="fc-btn fc-btn--ghost fc-btn--md" to="/account">
            {t("Change it")}
          </Link>
          <Button variant="ghost" onClick={() => void auth.signOut()}>
            {t("Sign out")}
          </Button>
        </div>

        {/* The most common reason a confirmation never arrives. */}
        <p className="fc-verify-gate__hint">
          Not there? Check spam — and give it a minute before asking again. If
          the address above is wrong, change it.
        </p>
      </div>
    </div>
  );
}
